import { tokenPrefix } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

// Composition styles for the rebuilt Tour Plans panel. The rail chrome
// (gutters, header/footer dividers, scroll region) belongs to the dark
// SidePanel; what lives here is the content inside those slots. Colors are
// dark-package semantic tokens, resolved inside DarkProvider.

export const headerRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  minWidth: 0,
}

export const titleStyles = (colorScheme: ColorScheme) => ({
  textStyle: 'heading',
  color: `${tokenPrefix(colorScheme)}text.primary`,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const descriptionStyles = (colorScheme: ColorScheme) => ({
  textStyle: 'caption',
  color: `${tokenPrefix(colorScheme)}text.secondary`,
})

export const rowListStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}

export const emptyTextStyles = (colorScheme: ColorScheme) => ({
  textStyle: 'caption-medium',
  color: `${tokenPrefix(colorScheme)}text.secondary`,
  textAlign: 'center',
  my: '30px',
})

export const floorGroupHeaderStyles = (colorScheme: ColorScheme) => ({
  textStyle: 'body-bold',
  color: `${tokenPrefix(colorScheme)}text.primary`,
  pt: '16px',
  pb: '8px',
})

/**
 * The Active Incidents bar (features/notifications-tab/NotificationsTab) is
 * `position: fixed; bottom: 0; right: 0` at 450×42, so it sits on top of the
 * bottom of this rail. The form footers clear it the way the legacy panel did
 * (16px footer padding + 40px margin) — without this the Cancel / Create
 * buttons render underneath it and are unreachable.
 */
const ACTIVE_INCIDENTS_BAR_CLEARANCE = '42px'

// Footer actions: Cancel on the left, destructive + primary grouped right.
export const footerActionsStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
  pb: ACTIVE_INCIDENTS_BAR_CLEARANCE,
}

export const footerActionsGroupStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}

export const metaRowStyles = (colorScheme: ColorScheme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  textStyle: 'caption',
  color: `${tokenPrefix(colorScheme)}text.secondary`,
})

export const detailTitleStyles = (colorScheme: ColorScheme) => ({
  textStyle: 'label',
  color: `${tokenPrefix(colorScheme)}text.primary`,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

// Leading tile for the detail header, matching the FeedItem icon frame size
// used by the rows below it.
export const detailIconTileStyles = (colorScheme: ColorScheme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '32px',
  height: '32px',
  bg: `${tokenPrefix(colorScheme)}brand.primary`,
  color: `${tokenPrefix(colorScheme)}text.on-accent`,
})

// Row tile inside FeedItem's 30×30 icon frame — the "leading rounded tile"
// the mapping calls for, so rows read with the same weight as the checkpoints
// panel rather than as a bare glyph.
export const rowIconTileStyles = (colorScheme: ColorScheme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '30px',
  height: '30px',
  bg: `${tokenPrefix(colorScheme)}brand.primary`,
  color: `${tokenPrefix(colorScheme)}text.on-accent`,
})

// FeedItem's title wraps by default; plan and checkpoint names are single-line
// with an ellipsis, matching the mapping mockups.
export const rowTitleStyles = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

// Checked rows take the same selected darkening FeedItem uses; the highlight
// belongs to the row treatment, not the Checkbox itself (ENG-32001).
export const checkboxRowStyles = (
  colorScheme: ColorScheme,
  isChecked: boolean
) => {
  const prefix = tokenPrefix(colorScheme)
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    px: '12px',
    py: '10px',
    width: '100%',
    cursor: 'pointer',
    bg: isChecked ? `${prefix}bg.table-row-selected` : 'transparent',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderColor: `${prefix}border.subtle`,
    transition: 'background 150ms ease',
    _hover: {
      bg: isChecked
        ? `${prefix}bg.table-row-selected`
        : `${prefix}bg.table-row-hover`,
    },
  }
}

export const checkboxRowTitleStyles = (colorScheme: ColorScheme) => ({
  textStyle: 'caption-medium',
  color: `${tokenPrefix(colorScheme)}text.primary`,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const checkboxRowNoteStyles = (colorScheme: ColorScheme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  textStyle: 'caption',
  color: `${tokenPrefix(colorScheme)}text.secondary`,
})

export const skeletonRowStyles = {
  opacity: 0.4,
  pointerEvents: 'none',
}
